import {
  SOCIAL_PLATFORM_ORDER,
  socialPlatformLabel,
  type AnalyticsAccount,
  type SocialConnection,
} from '@mg/shared';

/**
 * Which accounts analytics can be filtered by — shared by the Analytics page and the Dashboard's
 * Engagement Overview, so the two offer the same pills and name an account by the same id.
 *
 * Analytics is per connected ACCOUNT, not per platform: two Facebook Pages are two pills. The
 * filter value is the connection's id; the server looks it up inside the workspace.
 */

/**
 * Every connected account, once, in platform order and then in the order the connections list
 * gives them (oldest first).
 *
 * A pending connection (authorised, no Page picked yet) is not an account. Two rows that hold the
 * SAME account — it was connected twice — are one pill: the server answers for either id with the
 * same numbers. A row with no platform id cannot be matched to another, so it stands on its own.
 */
export function analyticsAccounts(connections: readonly SocialConnection[]): AnalyticsAccount[] {
  const accounts: AnalyticsAccount[] = [];
  const seen = new Set<string>();
  for (const platform of SOCIAL_PLATFORM_ORDER) {
    for (const connection of connections) {
      if (connection.platform !== platform || !connection.connected || !connection.id) continue;
      const key = connection.externalId ? `${platform}:${connection.externalId}` : connection.id;
      if (seen.has(key)) continue;
      seen.add(key);
      accounts.push({
        connectionId: connection.id,
        platform,
        accountName: connection.accountName || socialPlatformLabel[platform],
        accountHandle: connection.accountHandle,
      });
    }
  }
  return accounts;
}

/** Connection ids are UUIDs; the server refuses anything else with a 400. */
const CONNECTION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The `?account=` URL value when it has the shape of a connection id, else undefined ("All
 * accounts"). A hand-edited or cut-off link must land on the page, not on an error.
 */
export function parseAccountParam(value: string | null): string | undefined {
  return value != null && CONNECTION_ID.test(value) ? value.toLowerCase() : undefined;
}

/**
 * The account to show: the requested one when it is among the connected accounts, else none ("All
 * accounts") — an account disconnected since the link was made must not leave an empty page.
 *
 * `accounts` is undefined while the connections are still loading. The request is then taken at
 * its word, so a good link starts loading its numbers at once instead of waiting for the list.
 */
export function pickAccount(
  requested: string | undefined,
  accounts: readonly AnalyticsAccount[] | undefined,
): string | undefined {
  if (requested == null || accounts == null) return requested;
  return accounts.some((a) => a.connectionId === requested) ? requested : undefined;
}
